import React, { ReactNode, useRef, useState } from "react";
import { Colors } from "../../utils/colors";
import { Carousel } from "react-responsive-carousel";

export interface CarouselItem {
  image: string;
  title: string;
  width?: number; // must pass in width if height is not passed in
  height?: number; // must pass in height if width is not passed in
  alternateItem?: ReactNode;
}

type CarouselContainerProps = {
  carouselItems: CarouselItem[];
  dark?: boolean;
  enableSwipe?: boolean;
  containerStyles?: React.CSSProperties;
  showMulti?: boolean;
  pauseOnHover?: boolean;
  hideFadedGradient?: boolean;
};

const CarouselContainer = (props: CarouselContainerProps) => {
  const {
    carouselItems,
    dark,
    enableSwipe,
    containerStyles,
    showMulti,
    pauseOnHover,
    hideFadedGradient,
  } = props;

  const [currentIndex, setCurrentIndex] = useState(0);
  const carouselRef = useRef<Carousel>(null);
  const touchStartX = useRef<number | null>(null);

  // Workaround for react-responsive-carousel centerMode swipe bug (#487):
  // In showMulti mode, we handle swipes manually to avoid library's broken logic.
  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const deltaX = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    const SWIPE_THRESHOLD = 40;
    if (Math.abs(deltaX) < SWIPE_THRESHOLD) return;
    const length = carouselItems.length;
    const next =
      deltaX < 0
        ? (currentIndex + 1) % length
        : (currentIndex - 1 + length) % length;
    carouselRef.current?.moveTo(next);
  };

  return (
    <div
      className="ColumnCentering"
      onTouchStart={showMulti && enableSwipe ? onTouchStart : undefined}
      onTouchEnd={showMulti && enableSwipe ? onTouchEnd : undefined}
      style={{
        gap: 30,
        margin: "auto",
        overflow: "hidden",
        position: "relative",
        ...containerStyles,
      }}
    >
      {showMulti && !hideFadedGradient ? (
        <>
          {/* Left fade overlay */}
          <div
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              width: 120,
              background:
                "linear-gradient(to right, rgba(0, 0, 0, 0.9), transparent)",
              zIndex: 2,
              pointerEvents: "none",
            }}
          />
          {/* Right fade overlay */}
          <div
            style={{
              position: "absolute",
              right: 0,
              top: 0,
              bottom: 0,
              width: 120,
              background:
                "linear-gradient(to left, rgba(0, 0, 0, 0.9), transparent)",
              zIndex: 2,
              pointerEvents: "none",
            }}
          />
        </>
      ) : null}
      <Carousel
        ref={carouselRef}
        showArrows={false}
        showStatus={false}
        showThumbs={false}
        showIndicators={false}
        stopOnHover={pauseOnHover}
        autoPlay={true}
        infiniteLoop
        interval={5000}
        transitionTime={500}
        swipeable={!showMulti && !!enableSwipe}
        emulateTouch={!showMulti && !!enableSwipe}
        onChange={(index) => setCurrentIndex(index)}
        centerMode={showMulti}
        centerSlidePercentage={40}
      >
        {carouselItems.map((item, index) =>
          item.alternateItem ? (
            <div
              key={index}
              className="ColumnCentering"
              style={{ marginInline: showMulti ? 14 : 0 }}
            >
              {item.alternateItem}
            </div>
          ) : (
            <div key={index} className="ColumnCentering" style={{ gap: 40 }}>
              <img
                src={item.image}
                alt={item.title}
                style={{
                  maxWidth: item.width ? item.width : "100%",
                  height: item.height ? item.height : "100%",
                  objectFit: "contain",
                }}
              />
              {item.title ? (
                <span
                  className="sectionTitle"
                  style={{
                    color: dark ? Colors.WHITE : Colors.BLACK,
                    maxWidth: 327,
                    wordWrap: "break-word",
                  }}
                >
                  {item.title}
                </span>
              ) : null}
            </div>
          )
        )}
      </Carousel>
      <div className="AlignedRow" style={{ gap: 7, justifyContent: "center" }}>
        {carouselItems.map((_, index) => (
          <button
            key={index}
            onClick={() => carouselRef.current?.moveTo(index)}
            disabled={!enableSwipe}
            style={{
              width: 10,
              height: 10,
              borderRadius: 100,
              backgroundColor:
                currentIndex === index
                  ? dark
                    ? Colors.GRAY15
                    : Colors.BLACK
                  : dark
                    ? Colors.BLACK1
                    : Colors.GRAY11,
              cursor: enableSwipe ? "pointer" : "default",
              border: "none",
              padding: 0,
              transition: "background-color 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default CarouselContainer;
